// Address completion protocol, shared by the app (src/backend/placesClient.ts) and the places-v2
// edge function (supabase/functions/places-v2). Owner 2026-09-14: the address field on New
// Inspection completes like Google Maps — a few characters, several candidates, one tap fills
// street / city / state / ZIP. The Google key lives ONLY on the server; the app sends the signed-in
// user's token, and without the server (offline, not configured) the field is a plain text field.
export const PLACES_PROTOCOL_VERSION = 1;
export const PLACES_FUNCTION_PATH = '/functions/v1/places-v2';
export const PLACES_INPUT_MIN = 3;
export const PLACES_INPUT_MAX = 120;
export const PLACES_MAX_SUGGESTIONS = 5;

export interface AddressSuggestion {
  placeId: string;
  // "62 Virginia Ave" · "Plainview, NY, USA"
  mainText: string;
  secondaryText: string;
}

export interface ResolvedAddress {
  address: string;
  city: string;
  state: string;
  zip: string;
  formatted: string;
}

export type PlacesRequest =
  | { action: 'autocomplete'; protocolVersion: number; input: string; sessionToken: string }
  | { action: 'details'; protocolVersion: number; placeId: string; sessionToken: string };

export type PlacesErrorCode = 'badRequest' | 'unauthenticated' | 'notConfigured' | 'upstream' | 'internalError';

export type PlacesResponse =
  | { ok: true; action: 'autocomplete'; suggestions: AddressSuggestion[] }
  | { ok: true; action: 'details'; address: ResolvedAddress }
  | { ok: false; code: PlacesErrorCode; message: string };

export const PLACES_ERROR_STATUS: Readonly<Record<PlacesErrorCode, number>> = Object.freeze({
  badRequest: 400,
  unauthenticated: 401,
  notConfigured: 503,
  upstream: 502,
  internalError: 500
});

export function placesError(code: PlacesErrorCode, message: string): PlacesResponse {
  return { ok: false, code, message };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function nonEmptyString(value: unknown, max: number): value is string {
  return typeof value === 'string' && value.trim().length > 0 && value.length <= max;
}

const SESSION_TOKEN_MAX = 64;
const PLACE_ID_MAX = 300;

export function checkPlacesRequest(body: unknown): { ok: true; request: PlacesRequest } | { ok: false; code: 'badRequest'; message: string } {
  if (!isRecord(body)) return { ok: false, code: 'badRequest', message: 'body must be an object' };
  if (body.protocolVersion !== PLACES_PROTOCOL_VERSION) return { ok: false, code: 'badRequest', message: 'unsupported protocolVersion' };
  if (!nonEmptyString(body.sessionToken, SESSION_TOKEN_MAX)) return { ok: false, code: 'badRequest', message: 'sessionToken is required' };
  if (body.action === 'autocomplete') {
    if (typeof body.input !== 'string' || body.input.trim().length < PLACES_INPUT_MIN) return { ok: false, code: 'badRequest', message: `input needs at least ${PLACES_INPUT_MIN} characters` };
    if (body.input.length > PLACES_INPUT_MAX) return { ok: false, code: 'badRequest', message: 'input is too long' };
    return { ok: true, request: { action: 'autocomplete', protocolVersion: PLACES_PROTOCOL_VERSION, input: body.input.trim(), sessionToken: body.sessionToken } };
  }
  if (body.action === 'details') {
    if (!nonEmptyString(body.placeId, PLACE_ID_MAX)) return { ok: false, code: 'badRequest', message: 'placeId is required' };
    return { ok: true, request: { action: 'details', protocolVersion: PLACES_PROTOCOL_VERSION, placeId: body.placeId, sessionToken: body.sessionToken } };
  }
  return { ok: false, code: 'badRequest', message: 'unknown action' };
}

const ERROR_CODES: readonly string[] = ['badRequest', 'unauthenticated', 'notConfigured', 'upstream', 'internalError'];

function isSuggestion(value: unknown): value is AddressSuggestion {
  return isRecord(value) && typeof value.placeId === 'string' && typeof value.mainText === 'string' && typeof value.secondaryText === 'string';
}

function isResolvedAddress(value: unknown): value is ResolvedAddress {
  return isRecord(value) && ['address', 'city', 'state', 'zip', 'formatted'].every((k) => typeof value[k] === 'string');
}

// The app's view of an answer: our protocol or nothing (a gateway page, a 404 when the function is
// not deployed). Never trusted beyond its shape.
export function checkPlacesResponse(value: unknown): PlacesResponse | null {
  if (!isRecord(value)) return null;
  if (value.ok === false) {
    return typeof value.code === 'string' && ERROR_CODES.includes(value.code) && typeof value.message === 'string' ? { ok: false, code: value.code as PlacesErrorCode, message: value.message } : null;
  }
  if (value.ok !== true) return null;
  if (value.action === 'autocomplete') {
    return Array.isArray(value.suggestions) && value.suggestions.every(isSuggestion) ? { ok: true, action: 'autocomplete', suggestions: value.suggestions.slice(0, PLACES_MAX_SUGGESTIONS) } : null;
  }
  if (value.action === 'details') {
    return isResolvedAddress(value.address) ? { ok: true, action: 'details', address: value.address } : null;
  }
  return null;
}
